/**
 * Tab 1 — Intro video (spec 2026-10-23 §5). The video that plays the first
 * time someone opens the app, before sign-up.
 *
 * Content (videos, descriptions, audience) is a DRAFT until Publish. On/off
 * is the switch, which publishes the LIVE version with `enabled` flipped.
 * Every change that reaches members goes through ConfirmDialog first, and
 * every publish sends the newest version this page has seen
 * (`based_on_version`), so a change made by someone else is a 409, never an
 * overwrite. The rules live in lib/intro-state.ts; this file wires them.
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from '@/hooks/useToast';
import type { AdminVersionRow } from './api';
import { AudienceField } from './components/AudienceField';
import { ConfirmDialog } from './components/ConfirmDialog';
import { HistoryList } from './components/HistoryList';
import { IntroSwitch, StatusChip } from './components/IntroSwitch';
import { LanguagePanel } from './components/LanguagePanel';
import { PublishBar } from './components/PublishBar';
import { discardRequest, publishRequest, restoreRequest, switchRequest, type ConfirmRequest } from './lib/confirm-requests';
import type { ExperienceLocale, IntroVideoConfig } from './lib/contract';
import {
  LOCALES,
  draftFrom,
  introConfigOf,
  introHistorySummary,
  introPosterOf,
  introPublishState,
  restorePlan,
  statusOf,
  switchState,
  type FinalizedMedia,
  type IntroDraft,
} from './lib/intro-state';
import { useLeaveWarning, type AppExperienceData } from './useAppExperience';

type Action = { kind: 'publish'; config: IntroVideoConfig; basedOn: number | null; resetDraft: boolean } | { kind: 'discard' };

interface Pending {
  request: ConfirmRequest;
  action: Action;
}

interface Props {
  data: AppExperienceData;
  rows: AdminVersionRow[];
  canWrite: boolean;
}

export function IntroVideoTab({ data, rows, canWrite }: Props) {
  const latest = rows[0] ?? null;
  const live = latest ? introConfigOf(latest.config) : null;
  const latestVersion = latest?.version ?? null;

  // null = no local edit: the draft follows the live version.
  const [edited, setEdited] = useState<IntroDraft | null>(null);
  const draft = edited ?? draftFrom(live);
  const pub = introPublishState({ live, draft, canWrite });
  const sw = switchState({ live, canWrite });
  const liveOn = live?.enabled ?? false;

  const [uploading, setUploading] = useState<Record<ExperienceLocale, boolean>>({ en: false, es: false });
  const anyUpload = uploading.en || uploading.es;
  useLeaveWarning(anyUpload || pub.dirty);

  const [pending, setPending] = useState<Pending | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const statusHeading = useRef<HTMLHeadingElement>(null);
  const focusStatusOnClose = useRef(false);

  const edit = (change: (d: IntroDraft) => IntroDraft) => setEdited((prev) => change(prev ?? draftFrom(live)));
  const setMedia = (loc: ExperienceLocale) => (media: FinalizedMedia | null) =>
    edit((d) => ({ ...d, videos: { ...d.videos, [loc]: media } }));
  const busyEn = useCallback((b: boolean) => setUploading((u) => ({ ...u, en: b })), []);
  const busyEs = useCallback((b: boolean) => setUploading((u) => ({ ...u, es: b })), []);

  const open = (request: ConfirmRequest, action: Action) => {
    setNote('');
    setActionError(null);
    setPending({ request, action });
  };
  const close = () => setPending(null);

  const openSwitch = () => {
    if (sw.next) open(switchRequest(sw, pub.dirty), { kind: 'publish', config: sw.next, basedOn: latestVersion, resetDraft: false });
  };
  const openPublish = () => {
    if (pub.config) open(publishRequest(pub, liveOn), { kind: 'publish', config: pub.config, basedOn: latestVersion, resetDraft: true });
  };
  const openRestore = (row: AdminVersionRow, config: IntroVideoConfig) => {
    if (latestVersion === null) return;
    const plan = restorePlan({ config, latestVersion, live, dirty: pub.dirty });
    open(restoreRequest(row.version, plan, liveOn), { kind: 'publish', config: plan.config, basedOn: plan.based_on_version, resetDraft: true });
  };
  const openDiscard = () => open(discardRequest(), { kind: 'discard' });

  const confirm = async () => {
    if (!pending) return;
    const { action, request } = pending;
    if (action.kind === 'discard') {
      setEdited(null);
      focusStatusOnClose.current = true;
      close();
      toast({ title: request.success });
      return;
    }
    setSubmitting(true);
    setActionError(null);
    const text = note.trim();
    const r = await data.publish('intro_video', { config: action.config, based_on_version: action.basedOn, note: text === '' ? null : text });
    setSubmitting(false);
    if (r.ok) {
      if (action.resetDraft) setEdited(null);
      focusStatusOnClose.current = true;
      close();
      toast({ title: request.success, description: `This is version ${r.version}.`, variant: 'success' });
    } else if (r.conflict) {
      close();
    } else {
      setActionError(r.message);
    }
  };

  return (
    <div className="space-y-6">
      <section className="space-y-4 rounded-lg border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 ref={statusHeading} tabIndex={-1} className="flex items-center gap-2 text-lg font-semibold focus:outline-none">
              Intro video <StatusChip status={statusOf(live)} />
            </h3>
            <p className="text-sm text-muted-foreground">
              A short video that plays the first time someone opens the app, before sign-up. It plays one time on each phone.
            </p>
            <p className="text-xs text-muted-foreground">Plays on app 1.0.5 and later. Older app versions do not show it.</p>
          </div>
          <IntroSwitch sw={sw} disabled={submitting} onToggle={openSwitch} />
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        {LOCALES.map((loc) => (
          <LanguagePanel
            key={loc}
            locale={loc}
            apiUrl={data.apiUrl}
            media={draft.videos[loc]}
            liveUrl={live?.videos[loc]?.url ?? null}
            description={draft.descriptions[loc]}
            canWrite={canWrite}
            onMedia={setMedia(loc)}
            onDescription={(text) => edit((d) => ({ ...d, descriptions: { ...d.descriptions, [loc]: text } }))}
            onBusyChange={loc === 'en' ? busyEn : busyEs}
          />
        ))}
      </div>

      <AudienceField value={draft.audiencePct} disabled={!canWrite} onChange={(v) => edit((d) => ({ ...d, audiencePct: v }))} />

      <PublishBar
        state={pub}
        hasLive={live !== null}
        liveOn={liveOn}
        canWrite={canWrite}
        busy={submitting || anyUpload}
        waitReason={anyUpload ? 'Wait until the upload is finished. Then publish.' : null}
        onPublish={openPublish}
        onDiscard={openDiscard}
      />

      <HistoryList
        rows={rows}
        whoName={data.whoName}
        canWrite={canWrite}
        busy={submitting || anyUpload}
        parse={introConfigOf}
        summarize={introHistorySummary}
        posterOf={introPosterOf}
        onRestore={openRestore}
        idPrefix="intro"
      />

      <ConfirmDialog
        request={pending?.request ?? null}
        note={note}
        onNoteChange={setNote}
        submitting={submitting}
        error={actionError}
        onCancel={close}
        onConfirm={() => void confirm()}
        onCloseAutoFocus={(e) => {
          if (!focusStatusOnClose.current) return;
          focusStatusOnClose.current = false;
          e.preventDefault();
          statusHeading.current?.focus();
        }}
      />
    </div>
  );
}
